import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useSocket } from '../contexts/SocketContext';
import { useAuth } from '../contexts/AuthContext';
import { getRankInfo } from '../rank';
import Home from './Home';

const API = process.env.REACT_APP_API_URL || '';

const HINTS = [
  "Pardner, take one full practice test a week. Don't you dare ride on without reviewing every wrong answer first.",
  "Build yerself some trail stamina by simulating real test conditions, breaks and all. Leave that phone holstered during 'em.",
  "Annotate every paragraph or two with just a few words, like brands on cattle. Enough to recall what happened, nothin' more.",
  "Map yer passage like you're scoutin' territory so you can ride straight to any evidence without wanderin' the plains.",
  "There's one right answer and three varmint decoys. The SAT never leaves a question without clear evidence to back the winner.",
  "Focus on what the passage flat-out says, not what it merely implies. Don't go readin' between the tumbleweeds.",
  "On paired evidence questions, wrangle the first question yourself before lettin' the line references push you around.",
  "Evidence's gotta CERTAINLY support an answer. If it only implies it, toss it out like a bad hand of cards.",
  "Strip out yer assumptions and biases when reading; only direct answers are worth their weight in gold dust.",
  "On vocabulary questions, ride back to the line and find yer own replacement word before you even glance at the choices.",
  "Trust yer instincts like a seasoned trailhand. Don't switch yer answer unless you got solid evidence against it.",
  "For literature passages, track character feelings and how they change, like watching a cowboy's mood shift after a long ride.",
  "For history passages, find the author's rhetorical trail. Ask what they're advocatin' for, then watch how they back it up.",
  "For science passages, follow the researchers' quest: what mystery they huntin', and what obstacles did they run into?",
  "A period and a semicolon are the same critter. If both show up in answer choices, you can eliminate 'em both without a second thought.",
  "Know yer transition words like you know yer horse. 'Conversely' contradicts, 'consequently' causes.",
  "For sentence placement questions, read the paragraph for idea flow and transitions before you ever peek at the answer choices.",
  "For 'should this be added' questions, ask yerself if it serves the passage's purpose or just wanders off the trail.",
  "If you're scorin' below 700 in math, ride back to the basics with Khan Academy and College Panda until the trail clears.",
  "Above 700 in math, speedrun both sections. Finish no-calc in 15 minutes and calculator in 45, then revise with fresh eyes.",
  "Never do math problems in yer head alone. Scratch it out on paper like markin' a trail map, or you'll lose yer way.",
  "Know Desmos like you know yer saddle. Get comfortable with it before test day so it don't buck you off.",
  "Careless mistakes are yer biggest outlaw in math, not hard concepts. Give each answer a 10-20 second check before movin' on.",
  "If you can't crack an RW question, pick the simplest answer. Occam's Razor cuts cleaner than a Bowie knife.",
  "If two answers got you stumped, flag the question, pick the simpler one, and keep on ridin'.",
  "Watch prediction videos before test day because they've been known to save a cowpoke 40 points or more.",
  "The night before the test, rest that brain like a horse before a long ride. Heavy studying the day before is a fool's errand.",
  "Read more in yer everyday life, partner. It's the surest long trail to a stronger vocabulary and reading score.",
  "If you already got an 800 in one section, don't waste yer bullets. Focus yer practice on the section that still needs wranglin'.",
  "The math section ain't asking hard concepts. It's asking easy ones in mysterious ways, so learn to see through the disguise.",
];

export default function Queue() {
  const { socket } = useSocket();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [elapsed, setElapsed] = useState(0);
  const [queueSize, setQueueSize] = useState(0);
  const [onlineCount, setOnlineCount] = useState(0);
  const [status, setStatus] = useState('joining');
  const [opponent, setOpponent] = useState(null);
  const [countdown, setCountdown] = useState(5);
  const intervalRef   = useRef(null);
  const countdownRef  = useRef(null);
  const startRef      = useRef(Date.now());
  const pendingNavRef = useRef(null);
  const hintIntervalRef = useRef(null);

  const [hintIndex, setHintIndex] = useState(() => Math.floor(Math.random() * HINTS.length));
  const [hintFading, setHintFading] = useState(false);
  const [crownFrame, setCrownFrame] = useState(1);
  const crownRef = useRef(null);

  // Poll /api/online-count (includes bots) every 10 s — same source as homepage counter
  useEffect(() => {
    const fetchCount = () => {
      axios.get(`${API}/api/online-count`).then(r => setOnlineCount(r.data.count)).catch(() => {});
    };
    fetchCount();
    const poll = setInterval(fetchCount, 10_000);
    return () => clearInterval(poll);
  }, []);

  useEffect(() => {
    if (!socket) return;

    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('gamedata_')) sessionStorage.removeItem(key);
    });

    socket.emit('queue:join');
    startRef.current = Date.now();

    intervalRef.current = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startRef.current) / 1000));
    }, 1000);

    socket.on('queue:joined', () => setStatus('waiting'));
    socket.on('queue:size', ({ size }) => setQueueSize(size));

    socket.on('game:start', (data) => {
      const { gameId } = data;
      setOpponent(data.opponent);
      sessionStorage.setItem(`gamedata_${gameId}`, JSON.stringify(data));
      setStatus('found');
      clearInterval(intervalRef.current);
      pendingNavRef.current = gameId;

      // Always count exactly 5 seconds from the moment this client receives
      // the event. Using the server's absolute gameStartsAt timestamp caused
      // instant or sub-3s countdowns whenever the client clock was ahead of
      // the server (clock skew), or for bot matches where the event arrives
      // with minimal network delay but any skew wiped out the window.
      const navigateAt = Date.now() + 5000;
      setCountdown(5);
      const tick = () => {
        const remaining = Math.ceil((navigateAt - Date.now()) / 1000);
        if (remaining <= 0) {
          clearInterval(countdownRef.current);
          navigate(`/game/${gameId}`);
        } else {
          setCountdown(remaining);
        }
      };
      countdownRef.current = setInterval(tick, 250); // poll at 250 ms for snappy updates
    });

    socket.on('game:reconnect', ({ gameId }) => {
      navigate(`/game/${gameId}`);
    });

    return () => {
      clearInterval(intervalRef.current);
      clearInterval(countdownRef.current);
      socket.off('queue:joined');
      socket.off('queue:size');
      socket.off('game:start');
      socket.off('game:reconnect');
    };
  }, [socket, navigate]);

  useEffect(() => {
    crownRef.current = setInterval(() => {
      setCrownFrame(f => (f % 4) + 1);
    }, 700);
    return () => clearInterval(crownRef.current);
  }, []);

  useEffect(() => {
    if (status !== 'waiting') return;
    hintIntervalRef.current = setInterval(() => {
      setHintFading(true);
      setTimeout(() => {
        setHintIndex(i => (i + 1) % HINTS.length);
        setHintFading(false);
      }, 400);
    }, 10000);
    return () => clearInterval(hintIntervalRef.current);
  }, [status]);

  const handleLeave = () => {
    socket?.emit('queue:leave');
    navigate('/');
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  return (
    <div style={{ position: 'relative', minHeight: 'calc(100vh - 60px)' }}>
      {/* Blurred, dimmed, non-interactive homepage in the background */}
      <div className="queue-home-bg" style={{
        position: 'fixed', top: 60, left: 0, right: 0, bottom: 0,
        pointerEvents: 'none', userSelect: 'none',
        overflow: 'hidden', zIndex: 0,
      }}>
        <div style={{ opacity: 0.76 }}>
          <Home />
        </div>
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(15,23,42,0.2)' }} />
      </div>

      {/* Queue card on top */}
      <div className="queue-page" style={{ position: 'relative', zIndex: 1 }}>
      <div className="queue-card">
        {status === 'found' ? (
          <div className="queue-match-found">
            <div className="queue-vs-row">
              <div className="queue-vs-player">
                {user?.avatar
                  ? <img src={user.avatar} alt="" className="queue-vs-avatar" />
                  : <div className="queue-vs-avatar queue-vs-initials">{(user?.display_name || user?.name)?.charAt(0).toUpperCase()}</div>
                }
                {user != null && (() => {
                  const r = getRankInfo(user.rating ?? 0);
                  return (
                    <div className="queue-vs-name" style={{ display: 'flex', alignItems: 'center', gap: 5, maxWidth: '100%' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 3,
                        background: `${r.color}22`, border: `1.5px solid ${r.color}55`,
                        borderRadius: 6, padding: '2px 6px', fontSize: 12, fontWeight: 800,
                        color: r.color, flexShrink: 0,
                      }}>
                        {r.level}
                        <img src={r.icon} alt={r.label} style={{ width: 18, height: 18, objectFit: 'contain' }} />
                      </span>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                        {user?.display_name || user?.name}
                      </span>
                    </div>
                  );
                })()}
                {user?.school && <span className="school-flair school-flair-sm">{user.school}</span>}
              </div>
              <div className="queue-vs-badge">VS</div>
              <div className="queue-vs-player">
                {opponent?.avatar
                  ? <img src={opponent.avatar} alt="" className="queue-vs-avatar" />
                  : <div className="queue-vs-avatar queue-vs-initials">{opponent?.name?.charAt(0).toUpperCase() ?? '?'}</div>
                }
                {opponent?.rating != null && (() => {
                  const r = getRankInfo(opponent.rating);
                  return (
                    <div className="queue-vs-name" style={{ display: 'flex', alignItems: 'center', gap: 5, maxWidth: '100%' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: 3,
                        background: `${r.color}22`, border: `1.5px solid ${r.color}55`,
                        borderRadius: 6, padding: '2px 6px', fontSize: 12, fontWeight: 800,
                        color: r.color, flexShrink: 0,
                      }}>
                        {r.level}
                        <img src={r.icon} alt={r.label} style={{ width: 18, height: 18, objectFit: 'contain' }} />
                      </span>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                        {opponent?.name ?? 'Opponent'}
                      </span>
                    </div>
                  );
                })()}
                {!opponent?.rating && (
                  <div className="queue-vs-name" style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>
                    {opponent?.name ?? 'Opponent'}
                  </div>
                )}
                {opponent?.school && <span className="school-flair school-flair-sm">{opponent.school}</span>}
              </div>
            </div>
            <div className="queue-title" style={{ marginTop: 20 }}>Match Found!</div>
            <div className="queue-subtitle">Starting in {countdown}…</div>
            <div className="queue-countdown-dots">
              {[5, 4, 3, 2, 1].map(n => (
                <div key={n} className={`queue-countdown-dot${countdown <= n ? ' active' : ''}`} />
              ))}
            </div>
          </div>
        ) : (
          <>
            <img
              src={`/Crown${crownFrame}.png`}
              alt="Searching..."
              style={{ width: 90, height: 90, display: 'block', margin: '0 auto 4px', imageRendering: 'auto' }}
            />

            <div className="queue-title">
              {status === 'joining' ? 'Joining Queue…' : <>Finding Opponent<span className="ellipsis-anim" /></>}
            </div>
            <div className="queue-subtitle">
              {status === 'waiting'
                ? (() => {
                    const r = getRankInfo(user?.rating ?? 0);
                    return <>
                      <img src={r.icon} alt={r.label} className="queue-rank-icon" style={{ verticalAlign: 'middle', marginRight: 6 }} />
                      <span style={{ color: r.color, fontWeight: 700 }}>{r.label}</span>
                      {user?.school && <span className="school-flair school-flair-sm" style={{ marginLeft: 8 }}>{user.school}</span>}
                    </>;
                  })()
                : 'Connecting to matchmaking server…'}
            </div>

            {status === 'waiting' && (
              <>
                <div className="queue-timer">{formatTime(elapsed)}</div>
                <div className="queue-size">{onlineCount} player{onlineCount !== 1 ? 's' : ''} SATing<span className="ellipsis-anim" /></div>

                <div style={{ marginTop: 24, padding: '14px 16px', background: 'var(--cb-gray-100)', borderRadius: 8, fontSize: 13, color: 'var(--cb-gray-600)', textAlign: 'center', minHeight: 80, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <span style={{
                    opacity: hintFading ? 0 : 1,
                    transition: 'opacity 0.4s ease',
                    lineHeight: 1.55,
                    display: 'block',
                    fontStyle: 'italic',
                  }}>
                    "{HINTS[hintIndex]}"
                  </span>
                </div>

                <button className="btn btn-ghost" style={{ marginTop: 20, width: '100%' }} onClick={handleLeave}>
                  Cancel
                </button>
              </>
            )}
          </>
        )}
      </div>
      </div>
    </div>
  );
}
